import { PostPage } from "@/components/formats/post-page";
import { MemeGrid, MobileBackBtn, TitleShadow } from "@/components/styles";
import MemeTemplate from "@/components/ui/meme-template";
import useUserStore from "@/state/user/useUserStore";
import { Asset } from "@/types/types";
import React from "react";
import {
  ArrowBigLeft,
  Disc3,
  Eye,
  GraduationCap,
  Shirt,
  Smile,
} from "lucide-react";
import { BannerPage } from "@/components/formats/banner-page";
import { PostPageMobile } from "@/components/formats/post-page-mobile";
import Image from "@/components/ui/image";
import Link from "next/link";

const MobilePage = ({ disabled }: { disabled?: boolean }) => {
  const [assetsCount, imageFormat] = useUserStore((state) => [
    state.assetsCount,
    state.imageFormat,
  ]);

  const TemplateAssets = Array.from(
    { length: assetsCount?.template },
    (_, i) => `/Mememaker/template/template${i + 1}.png`
  );

  const HeadAssets = Array.from(
    { length: assetsCount?.base },
    (_, i) => `/Mememaker/base/base${i + 1}.png`
  );

  const ClothingAssets = Array.from(
    { length: assetsCount?.clothing },
    (_, i) => `/Mememaker/clothing/clothing${i + 1}.png`
  );

  const FaceAssets = Array.from(
    { length: assetsCount?.headgear },
    (_, i) => `/Mememaker/headgear/headgear${i + 1}.png`
  );

  const EyeAssets = Array.from(
    { length: assetsCount?.face },
    (_, i) => `/Mememaker/face/face${i + 1}.png`
  );

  const MiscAssets = Array.from(
    { length: assetsCount?.misc },
    (_, i) => `/Mememaker/misc/misc${i + 1}.png`
  ).reverse();

  const allAssets: Asset[] = [
    { assets: HeadAssets, icon: <Smile /> },
    { assets: ClothingAssets, icon: <Shirt /> },
    { assets: FaceAssets, icon: <GraduationCap /> },
    { assets: EyeAssets, icon: <Eye /> },
    { assets: MiscAssets, icon: <Disc3 /> },
  ];

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {disabled ? (
        <>
          <div
            style={{
              width: "4rem",
              height: "4rem",
              position: "relative",
            }}
          >
            <Image
              src={"/Mememaker/beraflip/beraflip.png"}
              alt={"beraflip"}
              fill
              style={{
                objectFit: "contain",
              }}
            />
          </div>
          <p
            style={{
              margin: 0,
              fontFamily: "var(--font-cabin-condensed)",
              fontSize: "20px",
              height: "3rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              color: "#000",
              textAlign: "center",
            }}
          >
            Please access on desktop for the best experience.
          </p>
        </>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {(() => {
            switch (imageFormat) {
              case "banner":
                return (
                  <BannerPage
                    color={"#eeba02"}
                    assets={allAssets}
                    websiteLink="https://www.0xhoneyjar.xyz/"
                  />
                );
              case "post":
                return (
                  <PostPageMobile
                    color={"#eeba02"}
                    assets={allAssets}
                    websiteLink="https://www.0xhoneyjar.xyz/"
                  />
                );
              default:
                return (
                  <div
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "flex",
                      flexDirection: "column",
                      boxSizing: "border-box",
                      padding: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "start",
                          justifyContent: "center",
                          flexDirection: "column",
                        }}
                      >
                        <TitleShadow style={{ margin: 0, marginBottom: "4px" }}>
                          Create
                        </TitleShadow>
                        <p
                          style={{
                            margin: 0,
                            fontFamily: "var(--font-cabin-condensed)",
                          }}
                        >
                          Select a preset meme template or upload your own.
                        </p>
                      </div>
                      <Link
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          fontFamily: "var(--font-cabin-condensed)",
                          whiteSpace: "nowrap",
                          textDecoration: "none",
                          color: "black",
                        }}
                        href="/"
                      >
                        <MobileBackBtn
                          whileHover={{
                            scale: 1.02,
                            transition: {
                              duration: 0.15,
                              ease: "linear",
                            },
                          }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <ArrowBigLeft size={20} />
                        </MobileBackBtn>
                      </Link>
                    </div>
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "row",
                        gap: "16px",
                      }}
                    >
                      <MemeGrid>
                        {assetsCount?.template > 0 && (
                          <>
                            {TemplateAssets.slice()
                              .reverse()
                              .map((src) => (
                                <MemeTemplate key={src} src={src} />
                              ))}
                            <MemeTemplate
                              src={"/Mememaker/background/memeblank1.png"}
                            />
                            <MemeTemplate
                              src={"/Mememaker/background/memeblank2.png"}
                            />
                            <MemeTemplate
                              src={"/Mememaker/background/memeblank3.png"}
                            />
                            <MemeTemplate
                              src={"/Mememaker/background/memeblank4.png"}
                            />
                          </>
                        )}
                      </MemeGrid>
                    </div>
                  </div>
                );
            }
          })()}
        </div>
      )}
    </div>
  );
};

export default MobilePage;
